import { ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { Box, Tab, Tabs as MuiTabs } from '@mui/material';
import Select from '../select/Select';
import Card from '../card/Card';

type TabItem = {
  label: ReactNode;
  content: ReactNode;
};

type Props = {
  id?: string;
  tabs: Array<TabItem>;
  activeTabIndex?: number;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  ariaLabel?: string;
  screen: string;
};

const tabId = (baseId: string, index: number) => `${baseId}-tab-${index}`;
const tabPanelId = (baseId: string, index: number) => `${baseId}-panel-${index}`;

export default function Tabs({
  id,
  tabs,
  activeTabIndex,
  variant = 'fullWidth',
  ariaLabel,
  screen,
}: Props) {
  const [value, setValue] = useState<string>(activeTabIndex?.toString() || '0');
  const baseId = id || `${screen}-tabs`;

  useEffect(() => {
    activeTabIndex && setValue(activeTabIndex.toString());
  }, [activeTabIndex]);

  return (
    <Box id={id} sx={{ width: '100%', typography: 'body1' }}>
      <Box display={{ xs: 'block', sm: 'none' }} p="1rem">
        <Select
          action={`${screen}-tab`}
          value={value}
          label={ariaLabel || ''}
          ariaLabel={ariaLabel || undefined}
          onChange={(newValue: string) => {
            setValue(newValue);
          }}
          options={tabs.map((tab, index) => ({
            label: tab.label,
            value: index.toString(),
          }))}
        />
      </Box>
      <Card>
        <Box display={{ xs: 'none', sm: 'block' }} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <MuiTabs
            value={value}
            variant={variant}
            aria-label={ariaLabel || 'Tabbed content'}
            onChange={(_event: SyntheticEvent, newValue: string) => {
              setValue(newValue);
            }}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                id={tabId(baseId, index)}
                aria-controls={tabPanelId(baseId, index)}
                label={tab.label}
                value={index.toString()}
                sx={{ '&.Mui-selected': { color: 'orange.600' } }}
              />
            ))}
          </MuiTabs>
        </Box>
        {tabs.map((tab, index) => {
          const tabValue = index.toString();
          const isSelected = value === tabValue;

          return (
            <Box
              key={index}
              role="tabpanel"
              hidden={!isSelected}
              id={tabPanelId(baseId, index)}
              aria-labelledby={tabId(baseId, index)}
            >
              {isSelected && tab.content}
            </Box>
          );
        })}
      </Card>
    </Box>
  );
}
